/**
 * RoleMediaForm — the MULTI-SLOT photo FORM (registry L2, family `control`).
 *
 * What a capture needs when the model is not "one photo" but "THIS photo for
 * THIS role and THAT one for THAT role": the multi-material template setup
 * (scene-studio's heavenly-reunion / angel-dog-reunion, two user photos plus a
 * fixed composition reference). `MediaPicker` cannot say it — its photos are an
 * unordered bag of up to `max`, and a list with no roles handed to the model is
 * the model guessing who is who.
 *
 * IT DRAWS THE WHOLE BODY of prod's `template_upload.html`, not just the wells:
 * the headline, the intro line, the `Selection order` / `Photo guide` heading
 * and the numbered role cards. Those sentences are COMPUTED FROM THE ROLE COUNT
 * — "Add 2 clear photos", "each reference in its named role" — so they are the
 * component's, not the blueprint's: three `Text` nodes carrying the same count
 * would be three places for the two runtimes to word it differently, and the
 * page has no way to pluralise. The copy is deliberately NOT configurable for
 * the same reason: one label prop per kit is one more way for the two kits to
 * say different words. The page keeps the template card above and the pinned
 * submit below.
 *
 * IT OWNS THE ROLE→MEDIA MAP. The interpreter grows no writable map for this:
 * a page-local `Map<roleId, url>` that only one module reads is that module's
 * state. What leaves the component is the ORDERED answer (`changed`), which is
 * exactly the shape the submit needs — the same arrangement
 * `SlotBoard.selectionChanged` uses to feed a builder's submit.
 *
 * SELF-CORRELATED PICKS. A tap emits `pickRequested {role_id,…}` and remembers
 * WHICH well it is picking for; the host's answer arrives on `setValue {url}`
 * and lands in that well. ONE pick at a time (prod: `if (pickingRole) return`)
 * — two in-flight requests share the one correlation slot and the second answer
 * would land in the first one's well. An explicit `role_id` on the delivery
 * still wins, so a blueprint that echoes the request back (the interpreter's
 * `host.hostResult(pickMedia)` op selector carries `request`) can address a well
 * directly.
 *
 * THE 90s CLOCK is the other half of that, and it is not a nicety: a picker the
 * user cancels answers NOTHING, so without it a well stays "Uploading…" forever
 * and the submit can never unlock.
 *
 * `submit_label` rides on `changed` because only the form knows how many wells
 * are filled, and it is emitted ONCE ON MOUNT: the submit's label is a pure
 * binding on `page_args.submit_label`, so an empty form must still word its
 * button before the first tap.
 *
 * TWIN: `flutter-neweyes-ai/lib/modules/pages/widget/native/view_ir/components/
 * role_media_form.dart` — every string and every measurement below is shared
 * with it value for value.
 *
 * SEALED: display only picks media — it never writes user data.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useViewIR } from '../viewIRContext';
import { LocalMediaDraft } from '../localMediaDraft';
import { Icon } from './primitives';
import { ImageRegionEditor } from './ImageRegionEditor';
import { MEDIA_INK, MEDIA_OVERLAY_STRONG, resetButton, toneColor } from './ui';

/** How long a well waits for the host's answer before it stops waiting. */
export const PICK_TIMEOUT_MS = 90000;

/** Prod's copy, verbatim. Not props: two label props = two kits, two wordings. */
const ADD_LABEL = 'Add image';
const REPLACE_LABEL = 'Replace';
const BUSY_LABEL = 'Uploading…';
const TIMEOUT_LABEL = 'The photo picker timed out. Please try again.';
const ERROR_LABEL = 'Unable to add that photo. Please try again.';
const TIP_LABEL = 'Clear, front-facing photos provide the best identity match.';

/** The fixed composition reference's default name in `role_ids`. */
const COMPOSITION_ROLE = 'composition_reference';

/** The headline (prod `introTitle`, the branch with no option groups). */
const titleFor = (n) => `Add ${n} clear photo${n === 1 ? '' : 's'}`;

/** The line under it (prod `.intro p`). */
const introFor = (n) => `Add ${n === 1 ? 'the reference below' : 'each reference in its named role'} and start the session here.`;

/**
 * The eyebrow over the wells (prod `.order-label`).
 *
 * With several wells the ORDER is the content ("Selection order"); with one
 * there is no order to state, so it falls back to a description ("Photo
 * guide"). Prod reads `roles.length > 1 ? … : …` — do not write it backwards.
 */
const orderHeadingFor = (n) => (n > 1 ? 'Selection order' : 'Photo guide');

/**
 * The submit's three-state wording (prod `#continue`'s `textContent`).
 *
 * The FORM computes it because only the form knows how many wells are filled;
 * the page parks it in `page_args.submit_label` and the button is a pure
 * binding. `readyLabel` is the one word a blueprint may override — the READY
 * state names what pressing the button does, which is the page's business, not
 * the kit's — and it defaults to prod's own copy so a page that says nothing
 * renders exactly what the bundle did.
 */
const submitLabelFor = (ready, total, readyLabel) => {
  if (total > 0 && ready === total) return readyLabel || 'Create scene';
  if (ready > 0) return `${ready} of ${total} added`;
  return `Add photo${total === 1 ? '' : 's'} above`;
};

/** The well column's width (prod `--editorial-photo`). */
const WELL_WIDTH = 104;
/** One card's minimum height (prod `.role { min-height: 146px }`). */
const CARD_MIN_HEIGHT = 146;

/**
 * `roles` → the wells this form draws, in order.
 *
 * Three shapes, all of them prod's: an object row (what the registry declares),
 * a bare STRING (what prod's `rolesFor()` falls back to from `materials` — the
 * string is the title), and the two mixed. The id defaults to `reference_<n>`
 * exactly as prod does: `role_ids` is the semantic order handed to the model
 * alongside the images, and a missing id would call photo 2 photo 1.
 */
function normalizeRoles(raw) {
  const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
  const out = [];
  list.forEach((row, i) => {
    const fallbackId = `reference_${i + 1}`;
    const fallbackLabel = `Reference ${i + 1}`;
    if (row && typeof row === 'object') {
      out.push({
        id: String(row.id ?? row.role_id ?? row.key ?? fallbackId),
        label: row.label == null || row.label === '' ? fallbackLabel : String(row.label),
        title: row.title == null || row.title === '' ? null : String(row.title),
        description: row.description == null || row.description === '' ? null : String(row.description),
      });
      return;
    }
    if (row == null || row === '') return;
    out.push({ id: fallbackId, label: fallbackLabel, title: String(row), description: null });
  });
  return out;
}

/** A media ref in any of the shapes a bind lands in → the url, or ''. */
function refUrl(value) {
  if (Array.isArray(value)) {
    for (const v of value) {
      const found = refUrl(v);
      if (found) return found;
    }
    return '';
  }
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const v = value.url ?? value.image_url ?? value.src ?? value.photo_url;
    return typeof v === 'string' ? v : '';
  }
  return '';
}

/** `{…, [key]: v}` minus one key, without mutating the original. */
function omit(map, key) {
  const next = { ...map };
  delete next[key];
  return next;
}

/**
 * ONE handler run per DELIVERY (`seq`), never per payload: pushing the same
 * photo into the same well twice is two answers from the picker, and the second
 * must not be swallowed. Same rule `SlotBoard`'s `select` inbox obeys.
 *
 * A delivery ALREADY SITTING in the inbox at mount is not for this mount: it is
 * marked seen, never replayed. That is load-bearing, not tidiness — the slot
 * store OUTLIVES the page. `ViewIRPage` takes `pageId` as a prop and is never
 * remounted across a navigation, nothing ever dispatches the reducer's `reset`,
 * and the studio deliberately shares one store across its cards. So without
 * this, leaving `template_upload` and coming back would remount the form on top
 * of the previous visit's `setValue` and refill a well with the last session's
 * photo — and the `host.hostResult(pickMedia)` wiring echoes a `role_id` back,
 * which is exactly the payload shape that makes such a replay land.
 *
 * The Flutter twin's `initState` does the same, for the same reason
 * (`ViewIrController._slots` is controller-level and survives `pageId`).
 */
function useDelivery(inbox, handler) {
  const seen = useRef(null);
  const fn = useRef(handler);
  const mounted = useRef(false);
  useEffect(() => { fn.current = handler; });
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      seen.current = inbox?.seq;
      return;
    }
    if (!inbox || inbox.seq === seen.current) return;
    seen.current = inbox.seq;
    fn.current?.(inbox.payload || {});
  }, [inbox]);
}

/**
 * RoleMediaForm — N ordered role wells, each filled by ONE host `pickMedia`.
 *
 * Props: `roles` (bind → `[{id,label,title?,description?}]` or bare strings) ·
 * `max` (per-role count, default 1) · `tip` (defaults to prod's line and is
 * ALWAYS drawn) · `compositionReference` (bind → a FIXED media ref the user does
 * not pick) · `compositionPosition` (`before` | `after`) · `compositionRole`
 * (the id that fixed ref travels under in `role_ids`) · `submitLabel` (the READY
 * state's word).
 *
 * Signals: `pickRequested {role_id,index,max,media_type}` — wired to
 * `host.pickMedia`; `changed {role_urls,role_ids,filled,total,complete,
 * ready_count,submit_label}` on every value change AND once on mount;
 * `cleared {role_id}`.
 *
 * Slots: `setValue {url}` (the host's answer, self-correlated) · `setBusy
 * {role_id?}` · `setError {role_id?,message}` · `clear {role_id?}`.
 */
export function RoleMediaForm({ props = {}, data, emit, slots }) {
  const runtime = useViewIR();
  const deferred = props.uploadTiming === 'on_submit';
  const draft = deferred ? runtime?.host?.localMediaDraft : null;
  const [submitPhase, setSubmitPhase] = useState('idle');
  const submitting = submitPhase !== 'idle';
  const roles = useMemo(
    () => normalizeRoles(props.roles !== undefined ? props.roles : data),
    [props.roles, data],
  );
  // 绑定求值会重建数组；角色内容未变时不能再次广播，否则页面参数形成反馈循环。
  const roleSignature = JSON.stringify(roles);
  const max = Math.min(9, Math.max(1, Math.trunc(Number(props.max) || 1)));
  const rawFixedRef = refUrl(props.compositionReference);
  // 与浏览器展示图片的地址一致；生成请求不能把相对路径留给服务端解释。
  const fixedRef = rawFixedRef?.startsWith('/') ? new URL(rawFixedRef, window.location.origin).href : rawFixedRef;
  const fixedRole = String(props.compositionRole || COMPOSITION_ROLE);
  const fixedFirst = props.compositionPosition === 'before';
  const readyLabel = props.submitLabel ? String(props.submitLabel) : null;

  const [values, setValues] = useState({});
  const [busyRole, setBusyRole] = useState(null);
  // `{role_id, message}`; a null `role_id` with a message is the WHOLE form's.
  const [failure, setFailure] = useState(null);
  const [broken, setBroken] = useState({});
  // Guides belong to the exact source ref, never to a mutable role alone.
  const guides = useRef({});
  const [guideState, setGuideState] = useState({});
  const editing = useRef(null);
  const [editor, setEditor] = useState(null);
  const hasRegionSelection = props.regionSelection != null;
  const selectionSignature = JSON.stringify(Array.isArray(props.regionSelection?.roles) ? props.regionSelection.roles : []);
  const markable = useMemo(() => JSON.parse(selectionSignature), [selectionSignature]);
  const clearGuide = useCallback((roleId) => {
    const guide = guides.current[roleId];
    if (guide) draft?.remove(guide.ref);
    delete guides.current[roleId];
    setGuideState({ ...guides.current });
  }, [draft]);

  // The map the emitted answer is built from. A ref beside the state because
  // `changed` is emitted from an event/delivery, never from a state updater:
  // a setState callback that emits would fire twice under StrictMode.
  const held = useRef(values);
  const picking = useRef(null);
  const clock = useRef(null);
  const emitRef = useRef(emit);
  useEffect(() => { emitRef.current = emit; }, [emit]);

  const stopClock = useCallback(() => {
    if (clock.current) { clearTimeout(clock.current); clock.current = null; }
  }, []);
  useEffect(() => stopClock, [stopClock]);

  /**
   * THE ORDERED ANSWER. `role_urls` is role order — never pick order — with the
   * fixed composition reference prepended or appended, and `role_ids` runs
   * parallel to it so the capture can name what each image is for. `filled` /
   * `ready_count` count the USER's wells only: the fixed reference is not
   * something anyone can fill or fail to fill, it is just a POSITION in the list
   * that is handed over (prod `launchInputs()` reads it the same way).
   */
  const announce = useCallback((map) => {
    const ids = [];
    const urls = [];
    const regionRoles = [];
    for (const role of roles) {
      const url = map[role.id];
      if (!url) continue;
      ids.push(role.id);
      urls.push(url);
    }
    const filled = urls.length;
    if (fixedRef) {
      if (fixedFirst) { ids.unshift(fixedRole); urls.unshift(fixedRef); } else { ids.push(fixedRole); urls.push(fixedRef); }
    }
    for (const role of roles) {
      const guide = guides.current[role.id];
      const id = `${role.id}_region`;
      if (markable.includes(role.id) && guide && guide.source === map[role.id] && !ids.includes(id) && !roles.some((r) => r.id === id) && !draft?.rejected.has(guide.ref)) {
        ids.push(id); urls.push(guide.ref); regionRoles.push(role.id);
      }
    }
    const total = roles.length;
    // A well that is MID-PICK is not ready, even when every well already holds a
    // url: re-picking a filled well (Replace) must drop `complete` for the length
    // of the upload, or the submit stays live and fires `openCapture` with the
    // photo that is still going up (prod's `readyCount !== roles.length` does the
    // same the instant a well returns to "Uploading…"). `changed` is re-emitted on
    // every busy transition (pick start / settle / cancel / error / timeout) so
    // `page_args.form_complete` tracks it both ways.
    const busy = picking.current != null || editing.current != null || draft?.busy;
    emitRef.current?.('changed', {
      role_urls: urls,
      role_ids: ids,
      ...(hasRegionSelection ? { region_roles: regionRoles } : {}),
      filled,
      total,
      complete: total > 0 && filled === total && !busy,
      // the same number under the SUBMIT's name (prod `readyCount`), so one
      // wire is enough to word the button
      ready_count: filled,
      submit_label: ({ preparing: 'Preparing photos…', uploading: 'Uploading photos…', dispatching: 'Creating session…' })[draft?.phase]
        || submitLabelFor(filled, total, readyLabel),
    });
  }, [roles, fixedRef, fixedRole, fixedFirst, readyLabel, draft, markable, hasRegionSelection]);

  const announceRef = useRef(announce);
  useEffect(() => { announceRef.current = announce; }, [announce]);

  // 宿主阶段是唯一忙碌来源；失败后按实际完整性恢复表单文案。
  useEffect(() => {
    if (!draft) return undefined;
    const refresh = () => {
      const kept = Object.fromEntries(Object.entries(held.current).filter(([, ref]) => !draft.rejected.has(ref)));
      if (Object.keys(kept).length !== Object.keys(held.current).length) {
        held.current = kept;
        setValues(kept);
      }
      setSubmitPhase(draft.phase);
      setFailure(draft.error ? { role_id: null, message: draft.error } : null);
      announceRef.current(held.current);
    };
    refresh();
    return draft.subscribe(refresh);
  }, [draft]);

  useEffect(() => {
    if (!markable.length && !Object.keys(guides.current).length && !editing.current) return;
    Object.keys(guides.current).forEach((role) => { if (!markable.includes(role)) clearGuide(role); });
    if (editing.current && !markable.includes(editing.current.roleId)) {
      editing.current = null; setEditor(null);
    }
    announceRef.current(held.current);
  }, [markable, clearGuide]);

  const commit = useCallback((map) => {
    held.current = map;
    setValues(map);
    announce(map);
  }, [announce]);

  /**
   * THE EMPTY-FORM BROADCAST. The submit's label is a pure binding on
   * `page_args.submit_label`, so a form nobody has touched yet must still say
   * "Add photos above" — without this the user's first sight of the page is a
   * button wearing whatever the page's fallback happened to be.
   *
   * Keyed on the ROLE LIST, not on mount alone: the roles arrive with the page
   * args, and a native navigation can hand them over a beat after the page is
   * up. A mount-only broadcast would then have counted zero roles and left the
   * button saying "Add photo above" on a two-photo template. A form with no
   * roles yet draws nothing and claims nothing.
   *
   * 两端都比较角色内容，而不是解释器每次求值创建的数组身份，避免回写循环。
   */
  useEffect(() => {
    if (roles.length) announceRef.current(held.current);
  }, [roleSignature, roles.length]);

  /** The host answered for a well: fill it (or, with no url, just end the wait). */
  const settle = useCallback((roleId, url) => {
    stopClock();
    picking.current = null;
    setBusyRole(null);
    // a cancelled picker answers with nothing — the values are unchanged, but the
    // well is no longer busy, so re-announce to restore `complete` from what is
    // filled (a cancelled Replace leaves the old photo, and the submit live again)
    if (!roleId || !url) { announce(held.current); return; }
    setFailure(null);
    if (held.current[roleId] !== url) {
      clearGuide(roleId);
      if (editing.current?.roleId === roleId) { editing.current = null; setEditor(null); }
    }
    setBroken((map) => omit(map, roleId));
    commit({ ...held.current, [roleId]: url });
  }, [commit, stopClock, announce, clearGuide]);

  useDelivery(slots?.setValue, (payload) => {
    const roleId = payload.role_id ?? picking.current ?? null;
    settle(roleId, refUrl(payload.url ?? payload.result ?? payload.src));
  });

  useDelivery(slots?.setBusy, (payload) => {
    const roleId = payload.role_id ?? picking.current ?? null;
    if (roleId) setBusyRole(roleId);
  });

  useDelivery(slots?.setError, (payload) => {
    const roleId = payload.role_id ?? picking.current ?? null;
    stopClock();
    picking.current = null;
    setBusyRole(null);
    setFailure({ role_id: roleId, message: String(payload.message || ERROR_LABEL) });
    // the well failed → no longer busy; restore `complete` from what is filled
    announce(held.current);
  });

  useDelivery(slots?.clear, (payload) => {
    if (draft?.busy) return;
    const roleId = payload.role_id == null ? null : String(payload.role_id);
    if (roleId) clearGuide(roleId);
    else Object.keys(guides.current).forEach(clearGuide);
    editing.current = null; setEditor(null);
    if (roleId) draft?.remove(held.current[roleId]);
    else Object.values(held.current).forEach((ref) => draft?.remove(ref));
    stopClock();
    picking.current = null;
    setBusyRole(null);
    setFailure(null);
    commit(roleId ? omit(held.current, roleId) : {});
    // `cleared` names a WELL; clearing the whole form is not one well being
    // emptied, and `changed` above already said the form is empty.
    if (roleId) emitRef.current?.('cleared', { role_id: roleId });
  });

  const requestPick = (role, index) => {
    // ONE pick at a time (prod: `if (!role || pickingRole || starting) return`)
    // — two in-flight requests share the one correlation slot.
    if (picking.current || editing.current || draft?.busy) return;
    picking.current = role.id;
    setBusyRole(role.id);
    setFailure(null);
    stopClock();
    // a well just went in-flight → drop `complete` for the upload (this is what
    // disables the submit when a filled well is being Replaced)
    announce(held.current);
    if (!deferred) clock.current = setTimeout(() => {
      clock.current = null;
      if (picking.current !== role.id) return;
      picking.current = null;
      setBusyRole(null);
      setFailure({ role_id: role.id, message: TIMEOUT_LABEL });
      // the wait ended with nothing → restore readiness from what is still filled
      announce(held.current);
    }, PICK_TIMEOUT_MS);
    emit?.('pickRequested', {
      role_id: role.id, index, max, media_type: 'image',
      ...(deferred ? { uploadTiming: 'on_submit', previous_ref: held.current[role.id] || '' } : {}),
    });
  };

  const closeEditor = () => {
    editing.current = null; setEditor(null); announce(held.current);
  };
  const openEditor = (roleId) => {
    if (picking.current || editing.current || draft?.busy) return;
    const source = held.current[roleId];
    if (!draft?.preview(source)) return;
    const next = { roleId, source };
    editing.current = next; setEditor(next); announce(held.current);
  };
  const saveGuide = ({ blob, strokes }) => {
    const current = editing.current;
    if (!current || held.current[current.roleId] !== current.source || draft?.busy) { closeEditor(); return; }
    const ref = draft.stage(blob, guides.current[current.roleId]?.ref);
    if (!ref) return;
    guides.current[current.roleId] = { source: current.source, ref, strokes };
    setGuideState({ ...guides.current });
    closeEditor();
  };

  if (!roles.length) return null;

  const tip = props.tip == null || props.tip === '' ? TIP_LABEL : String(props.tip);
  const formError = failure && failure.role_id == null ? failure.message : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* the head: prod `.intro`. `h2`, not `h1` — the page shell owns the
          page's own heading, and two `h1`s is two answers to "what is this". */}
      <h2 style={{
        margin: 0, fontSize: 'var(--t-3xl)', fontWeight: 700, lineHeight: 1.05,
        letterSpacing: '-1.2px', color: 'var(--text)',
        fontFamily: 'var(--font-display)',
      }}>{titleFor(roles.length)}</h2>
      <p style={{
        margin: 'var(--sp-3) 0 0', fontSize: 'var(--t-md)', lineHeight: 1.5, color: 'var(--text-2)',
      }}>{introFor(roles.length)}</p>

      {/* the eyebrow and the hairline that runs off it (prod `.order-label`
          and its `::after`): these wells are IN AN ORDER, and the order is
          read by the model, so it is said out loud. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
        marginTop: 'var(--sp-6)', minWidth: 0,
      }}>
        <span style={{
          fontSize: 'var(--t-xs)', fontWeight: 700, letterSpacing: '1.3px',
          textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap',
        }}>{orderHeadingFor(roles.length)}</span>
        <span aria-hidden style={{ flex: 1, height: 1, background: 'var(--line)' }} />
      </div>

      {roles.map((role, i) => {
        const url = values[role.id] || '';
        const busy = busyRole === role.id;
        const errored = failure?.role_id === role.id;
        const heading = role.title || role.label;
        return (
          <div key={role.id} data-vi-role={role.id} style={{ marginTop: 'var(--sp-3)', minWidth: 0 }}>
            {/* ONE tappable card: the well IS half of it, because the question
                this card asks is "which photo is this role". */}
            <button
              type="button"
              // prod's own aria line, verbatim: "Add image for The late loved one"
              aria-label={`${url ? 'Replace' : 'Add'} image for ${heading}`}
              aria-pressed={Boolean(url)}
              aria-busy={busy}
              disabled={submitting || editor != null}
              onClick={busy || submitting ? undefined : () => requestPick(role, i)}
              style={{
                ...resetButton,
                display: 'flex', alignItems: 'stretch', width: '100%', minWidth: 0,
                background: 'var(--surface)',
                borderRadius: 'var(--r-xl)', overflow: 'hidden',
                boxShadow: 'var(--shadow-2)',
                border: `1px solid ${errored ? toneColor('bad') : (url ? 'var(--accent-line)' : 'var(--line)')}`,
              }}
            >
              <span style={{
                position: 'relative', flex: `0 0 ${WELL_WIDTH}px`, width: WELL_WIDTH,
                background: 'var(--surface-2)', display: 'block',
              }}>
                {busy ? (
                  <span style={wellBody}>
                    <span aria-hidden style={SPINNER} />
                    <span style={wellCaption}>{deferred ? 'Opening photos…' : BUSY_LABEL}</span>
                  </span>
                ) : url && !broken[role.id] ? (
                  <>
                    <img
                      src={LocalMediaDraft.isLocal(url) ? draft?.preview(url) || undefined : url}
                      alt=""
                      onError={() => setBroken((b) => ({ ...b, [role.id]: true }))}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    <span style={{
                      position: 'absolute', left: 'var(--sp-2)', right: 'var(--sp-2)', bottom: 'var(--sp-2)',
                      padding: '5px 0', textAlign: 'center',
                      borderRadius: 'var(--r-pill)', background: MEDIA_OVERLAY_STRONG, color: MEDIA_INK,
                      fontSize: 10, fontWeight: 700, lineHeight: 1.1,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{REPLACE_LABEL}</span>
                  </>
                ) : (
                  <span style={wellBody}>
                    <Icon props={{ name: 'photo', size: 28, tone: 'text-3' }} />
                    <span style={wellCaption}>{ADD_LABEL}</span>
                  </span>
                )}
                {/* the well's own edge (prod `.photo-well`'s `border-right`) */}
                <span aria-hidden style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: 1, background: 'var(--line)' }} />
                {/* the index badge IS the well's position: the model reads the
                    photos in order, so the user must see which one they are on. */}
                <span aria-hidden style={{
                  position: 'absolute', top: 'var(--sp-2)', left: 'var(--sp-2)',
                  width: 25, height: 25, borderRadius: 'var(--r-pill)',
                  background: 'var(--text)', color: 'var(--bg)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 'var(--t-xs)', fontWeight: 800, lineHeight: 1.1,
                }}>{i + 1}</span>
              </span>

              <span style={{
                flex: 1, minWidth: 0, minHeight: CARD_MIN_HEIGHT,
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
                padding: 'var(--sp-5) var(--sp-4)',
              }}>
                <span style={{
                  fontSize: 'var(--t-xs)', fontWeight: 700, letterSpacing: '1.4px',
                  textTransform: 'uppercase', color: 'var(--text-3)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{role.label}</span>
                {role.title ? (
                  <span style={{
                    marginTop: 'var(--sp-2)', fontSize: 'var(--t-xl)', fontWeight: 700,
                    lineHeight: 1.05, letterSpacing: '-0.6px', color: 'var(--text)',
                    fontFamily: 'var(--font-display)',
                  }}>{role.title}</span>
                ) : null}
                {role.description ? (
                  <span style={{
                    marginTop: 'var(--sp-2)', fontSize: 'var(--t-sm)', lineHeight: 1.42, color: 'var(--text-2)',
                  }}>{role.description}</span>
                ) : null}
              </span>
            </button>
            {draft && Array.isArray(markable) && markable.includes(role.id) && url && draft.preview(url) && !roles.some((r) => r.id === `${role.id}_region`) && fixedRole !== `${role.id}_region` ? (
              editor?.roleId === role.id ? <ImageRegionEditor key={editor.source}
                src={draft.preview(editor.source)} initialStrokes={guideState[role.id]?.strokes}
                onSave={saveGuide} onCancel={closeEditor} /> : <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                <button type="button" disabled={submitting || editor != null || busyRole != null} onClick={() => openEditor(role.id)}
                  style={{ ...resetButton, minHeight: 44, padding: '8px 14px', borderRadius: 'var(--r-pill)', border: '1px solid var(--line)', background: 'var(--surface)', fontWeight: 600 }}>
                  {guideState[role.id] ? 'Edit marked area' : 'Mark area'}
                </button>
                {guideState[role.id] && <button type="button" disabled={submitting || editor != null || busyRole != null}
                  onClick={() => { clearGuide(role.id); commit({ ...held.current }); }}
                  style={{ ...resetButton, minHeight: 44, padding: 8, color: 'var(--text-2)' }}>Remove area</button>}
              </div>
            ) : null}
            {errored ? (
              <div role="alert" style={{
                marginTop: 'var(--sp-1)', marginLeft: 'var(--sp-2)',
                fontSize: 'var(--t-xs)', lineHeight: 1.4, color: toneColor('bad'),
              }}>{failure.message}</div>
            ) : null}
          </div>
        );
      })}

      {formError ? (
        <div role="alert" style={{
          marginTop: 'var(--sp-2)', fontSize: 'var(--t-xs)', lineHeight: 1.4, color: toneColor('bad'),
        }}>{formError}</div>
      ) : null}

      {/* prod draws `.tip` unconditionally, so a page that supplies none still
          gets the line that tells the user what a good photo is. */}
      <div style={{
        marginTop: 'var(--sp-4)', fontSize: 'var(--t-xs)', lineHeight: 1.45, color: 'var(--text-3)',
      }}>{tip}</div>
    </div>
  );
}

/** The well's three lanes all fill the column — it is a COLUMN, not a thumbnail. */
const wellBody = {
  position: 'absolute', inset: 0,
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  gap: 'var(--sp-2)', padding: '0 var(--sp-2)', textAlign: 'center',
};

const wellCaption = {
  fontSize: 10, fontWeight: 700, color: 'var(--text-3)',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
};

/** The in-flight ring (prod's `.spinner`; the keyframe lives in KitKeyframes). */
const SPINNER = {
  width: 22, height: 22, borderRadius: '50%',
  border: '2px solid var(--line)', borderTopColor: 'var(--text-3)',
  animation: 'viNativeSpin 0.8s linear infinite',
};
